"use client";

import * as Sentry from "@sentry/nextjs";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { claimChunkLoadReload, isChunkLoadError } from "@/lib/chunk-load-recovery";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // The locale layout stays mounted around a segment error, so the intl context
  // is available here — the strings used to be hard-coded Russian, which showed
  // to every English visitor including getmintai.app.
  const t = useTranslations("common");
  const chunkLoadFailed = isChunkLoadError(error);

  useEffect(() => {
    Sentry.captureException(error, {
      tags: { area: "locale-error", chunk_load_error: String(chunkLoadFailed) },
    });
    if (!chunkLoadFailed) return;

    // Segment errors never reach global-error. Use the same durable cooldown
    // here so a missing dynamic import can recover without a refresh loop.
    try {
      if (!claimChunkLoadReload(error, window.sessionStorage)) return;
    } catch {
      // Storage itself may be blocked; leave the explicit retry in control.
      return;
    }
    const reload = () => window.location.reload();
    try {
      void Sentry.flush(500).then(reload, reload);
    } catch {
      reload();
    }
  }, [error, chunkLoadFailed]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 font-sans">
      <h2 className="text-xl font-semibold">{t("somethingWentWrong")}</h2>
      <button
        onClick={() => {
          if (chunkLoadFailed) window.location.reload();
          else reset();
        }}
        className="cursor-pointer rounded-lg bg-[#5B4CFF] px-6 py-2 text-white hover:opacity-90"
      >
        {t("retry")}
      </button>
    </div>
  );
}
